import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ExternalLink, Loader2, Image as ImageIcon, Heart, FolderPlus, Tag, Moon, Sun, Folder, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import ImageViewerModal from "@/components/ImageViewerModal";
import ImageHoverPreview from "@/components/ImageHoverPreview";
import { processImageUrlSync } from "@/utils/imageProxy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [nsfwMode, setNsfwMode] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [imageHoverPreview, setImageHoverPreview] = useState(true);
  
  // New collection creation states
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDesc, setNewCollectionDesc] = useState("");
  
  // Scroll to top button state
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Helper function to navigate back with preserved state - ALWAYS go to home
  const navigateBack = () => {
    // Always return to homepage with preserved state (never back to article detail)
    const returnPath = location.state?.returnPath || '/';
    // If returnPath is another article, go to home instead
    if (returnPath.startsWith('/article/')) {
      navigate('/');
    } else {
      navigate(returnPath);
    }
  };

  // Scrape article and get article ID
  const scrapeArticle = async (related) => {
    try {
      const response = await axios.post(`${API}/articles/scrape-by-url`, {
        url: related.url,
        source: article.source
      });
      
      if (response.data.article_id) {
        return response.data.article_id;
      }
      return null;
    } catch (error) {
      console.error("Error scraping article:", error);
      return null;
    }
  };

  // Handle related article click - scrape and navigate to article detail
  const handleRelatedArticleClick = async (related) => {
    try {
      // Show loading toast
      toast.loading(`Loading ${related.title}...`);
      
      const articleId = await scrapeArticle(related);
      
      toast.dismiss();
      
      if (articleId) {
        // Preserve the original returnPath from the first article
        // This ensures that when user navigates through multiple related articles,
        // pressing back will always return to the original home page state
        const originalReturnPath = location.state?.returnPath || '/';
        
        navigate(`/article/${articleId}`, {
          state: { 
            returnPath: originalReturnPath
          }
        });
        
        toast.success("Article loaded successfully");
      } else {
        toast.error("Failed to load article");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error loading related article:", error);
      toast.error(error.response?.data?.detail || "Failed to load article");
    }
  };

  useEffect(() => {
    // Scroll to top when article loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const savedNsfw = localStorage.getItem("nsfwMode") === "true";
    setNsfwMode(savedNsfw);
    fetchArticleDetail();
    fetchCollections();
    loadSettings();
    
    // Add scroll listener for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [id]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/image_hover_preview`);
      if (response.data.value !== undefined) {
        setImageHoverPreview(response.data.value === "true");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      // Use default if loading fails
    }
  };

  const fetchArticleDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
      toast.error("Failed to load article details");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  const toggleFavorite = async () => {
    try {
      const response = await axios.post(`${API}/favorites/toggle/${id}`);
      toast.success(response.data.message);
      setArticle({ ...article, is_favorite: response.data.is_favorite });
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    
    try {
      await axios.post(`${API}/tags/add`, {
        article_id: id,
        tag: newTag.trim()
      });
      toast.success("Tag added");
      setArticle({ ...article, tags: [...article.tags, newTag.trim().toLowerCase()] });
      setNewTag("");
    } catch (error) {
      console.error("Error adding tag:", error);
      toast.error(error.response?.data?.detail || "Failed to add tag");
    }
  };

  const removeTag = async (tag) => {
    try {
      await axios.post(`${API}/tags/remove`, {
        article_id: id,
        tag: tag
      });
      toast.success("Tag removed");
      setArticle({ ...article, tags: article.tags.filter(t => t !== tag) });
    } catch (error) {
      console.error("Error removing tag:", error);
      toast.error("Failed to remove tag");
    }
  };

  const addToCollection = async () => {
    if (!selectedCollection) {
      toast.error("Please select a collection");
      return;
    }

    try {
      await axios.post(`${API}/collections/add`, {
        article_id: id,
        collection_id: selectedCollection
      });
      toast.success("Added to collection");
      setShowAddDialog(false);
      setSelectedCollection("");
    } catch (error) {
      console.error("Error adding to collection:", error);
      toast.error(error.response?.data?.detail || "Failed to add to collection");
    }
  };
  
  const createNewCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error("Please enter a collection name");
      return;
    }
    
    try {
      // Create the collection
      const response = await axios.post(`${API}/collections`, {
        name: newCollectionName,
        description: newCollectionDesc
      });
      
      const newCollection = response.data;
      toast.success(`Collection "${newCollectionName}" created`);
      
      // Add article to the new collection
      await axios.post(`${API}/collections/add`, {
        article_id: id,
        collection_id: newCollection.id
      });
      
      toast.success("Article added to new collection");
      
      // Reset states
      setShowCreateCollection(false);
      setShowAddDialog(false);
      setNewCollectionName("");
      setNewCollectionDesc("");
      setSelectedCollection("");
      
      // Refresh collections list
      fetchCollections();
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error(error.response?.data?.detail || "Failed to create collection");
    }
  };
  
  const handleCollectionSelect = (value) => {
    if (value === "create_new") {
      setShowCreateCollection(true);
    } else {
      setSelectedCollection(value);
      setShowCreateCollection(false);
    }
  };

  const openImageViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-testid="article-loading">
        {/* Header Skeleton */}
        <div className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="h-6 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Title Skeleton */}
          <div className="mb-8">
            <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4 animate-pulse w-3/4" />
            <div className="flex flex-wrap items-center gap-4">
              <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            </div>
          </div>

          {/* Tags Skeleton */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="h-7 w-24 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="h-7 w-16 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
            </div>
          </div>

          {/* Images Grid Skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-gray-900" data-testid="article-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Article not found</h2>
          <Button onClick={navigateBack} data-testid="back-home-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={navigateBack}
              className="hover:bg-gray-100 dark:hover:bg-gray-800"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="original-link"
              >
                View Original
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8" data-testid="article-header">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 rounded-full text-sm font-semibold text-blue-700 dark:text-blue-300">
                  {article.source}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 leading-tight" data-testid="article-title">
                {article.title}
              </h1>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFavorite}
              className="shrink-0"
              data-testid="favorite-toggle"
            >
              <Heart className={`w-6 h-6 ${article.is_favorite ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>

          {/* Tags Section */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {article.tags && article.tags.map((tag, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="cursor-pointer hover:bg-red-100 dark:hover:bg-red-900"
                  onClick={() => removeTag(tag)}
                  data-testid={`tag-${idx}`}
                >
                  {tag} ×
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addTag()}
                className="flex-1"
                data-testid="tag-input"
              />
              <Button onClick={addTag} data-testid="add-tag-button">
                <Tag className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Collections Display */}
          {article.collections && article.collections.length > 0 && (
            <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Folder className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                  In Collections:
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {article.collections.map((collection, idx) => (
                  <Badge
                    key={idx}
                    className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700"
                  >
                    {collection}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Add to Collection */}
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setShowCreateCollection(false);
              setNewCollectionName("");
              setNewCollectionDesc("");
              setSelectedCollection("");
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="text-gray-700 dark:text-gray-300" data-testid="add-to-collection-trigger">
                <FolderPlus className="w-4 h-4 mr-2" />
                Add to Collection
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-to-collection-dialog">
              <DialogHeader>
                <DialogTitle>{showCreateCollection ? "Create New Collection" : "Add to Collection"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {!showCreateCollection ? (
                  <>
                    <Select value={selectedCollection} onValueChange={handleCollectionSelect}>
                      <SelectTrigger data-testid="collection-select">
                        <SelectValue placeholder="Select collection" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="create_new" className="text-blue-600 dark:text-blue-400 font-semibold">
                          + Create New Collection...
                        </SelectItem>
                        {collections.map((coll) => (
                          <SelectItem key={coll.id} value={coll.id}>
                            {coll.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={addToCollection} 
                      className="w-full" 
                      data-testid="confirm-add-button"
                      disabled={!selectedCollection}
                    >
                      Add to Collection
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Collection Name *</label>
                        <Input
                          placeholder="Enter collection name"
                          value={newCollectionName}
                          onChange={(e) => setNewCollectionName(e.target.value)}
                          data-testid="new-collection-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Description (optional)</label>
                        <Input
                          placeholder="Enter description"
                          value={newCollectionDesc}
                          onChange={(e) => setNewCollectionDesc(e.target.value)}
                          data-testid="new-collection-desc"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowCreateCollection(false);
                          setNewCollectionName("");
                          setNewCollectionDesc("");
                        }} 
                        className="flex-1 text-gray-700 dark:text-gray-300"
                      >
                        Back
                      </Button>
                      <Button 
                        onClick={createNewCollection} 
                        className="flex-1" 
                        data-testid="create-collection-button"
                        disabled={!newCollectionName.trim()}
                      >
                        Create & Add
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Images Section */}
        <div className="space-y-6" data-testid="article-images">
          {article.images && article.images.length > 0 ? (
            article.images.map((imageUrl, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl cursor-pointer"
                style={{ transition: "shadow 0.3s ease" }}
                onClick={() => openImageViewer(index)}
                data-testid={`image-container-${index}`}
              >
                <ImageHoverPreview
                  enabled={imageHoverPreview}
                  imageSrc={processImageUrlSync(imageUrl, article?.source)}
                  imageAlt={`${article.title} - Image ${index + 1}`}
                >
                  <img
                    src={processImageUrlSync(imageUrl, article?.source)}
                    alt={`${article.title} - Image ${index + 1}`}
                    className={`w-full h-auto ${nsfwMode ? 'nsfw-blur' : ''}`}
                    onError={(e) => {
                      e.target.parentElement.style.display = "none";
                    }}
                    loading="lazy"
                  />
                </ImageHoverPreview>
              </div>
            ))
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center" data-testid="no-images">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No images found</h3>
              <p className="text-gray-500">This article doesn't contain any images</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Button
            onClick={navigateBack}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full shadow-lg"
            data-testid="back-to-gallery-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Gallery
          </Button>
        </div>
        
        {/* Related Articles Section */}
        {article?.related_articles && article.related_articles.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 mt-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {article.related_articles.map((related, idx) => (
                  <a
                    key={idx}
                    href="#"
                    className="group bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:shadow-lg transition-all hover:scale-105 cursor-pointer block"
                    onClick={async (e) => {
                      e.preventDefault();
                      
                      // Check if user wants to open in new tab (Ctrl+Click, Cmd+Click)
                      if (e.ctrlKey || e.metaKey) {
                        // Scrape article first, then open in new tab
                        toast.loading(`Preparing article...`);
                        const articleId = await scrapeArticle(related);
                        toast.dismiss();
                        
                        if (articleId) {
                          window.open(`/article/${articleId}`, '_blank');
                        } else {
                          toast.error("Failed to load article");
                        }
                      } else {
                        // Normal click - navigate in same tab
                        handleRelatedArticleClick(related);
                      }
                    }}
                    onAuxClick={async (e) => {
                      // Middle mouse button click - scrape and open in new tab
                      if (e.button === 1) {
                        e.preventDefault();
                        toast.loading(`Preparing article...`);
                        const articleId = await scrapeArticle(related);
                        toast.dismiss();
                        
                        if (articleId) {
                          window.open(`/article/${articleId}`, '_blank');
                        } else {
                          toast.error("Failed to load article");
                        }
                      }
                    }}
                  >
                    <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 relative overflow-hidden">
                      {related.thumbnail ? (
                        <img
                          src={processImageUrlSync(related.thumbnail, article.source)}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {related.title}
                      </h3>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        images={(article?.images || []).map(img => processImageUrlSync(img, article?.source))}
        initialIndex={viewerIndex}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
      
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-blue-600/50 hover:bg-blue-600/70 text-white shadow-lg transition-all duration-300 backdrop-blur-sm"
          title="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default ArticleDetail;